/**
 * Phase 20 Plan 20-02 — AddPhoneDialog
 *
 * RHF + zod phone validation mirroring backend D-190.
 * Fields: phoneInput (text), isPrimary (checkbox).
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAddPhoneMutation } from '@/hooks/useNotificationsQueries';
import { toast } from '@/lib/toast';
import { phoneInputSchema } from '@/lib/validation/phone-schema';

const addPhoneFormSchema = z.object({
  phoneInput: phoneInputSchema,
  isPrimary: z.boolean().default(false),
});

type AddPhoneFormValues = z.infer<typeof addPhoneFormSchema>;

interface AddPhoneDialogProps {
  readonly open: boolean;
  readonly recipientId: number;
  readonly onClose: () => void;
}

export function AddPhoneDialog({ open, recipientId, onClose }: AddPhoneDialogProps): JSX.Element {
  const addMutation = useAddPhoneMutation();

  const form = useForm<AddPhoneFormValues>({
    resolver: zodResolver(addPhoneFormSchema),
    defaultValues: { phoneInput: '', isPrimary: false },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: AddPhoneFormValues): Promise<void> => {
    try {
      await addMutation.mutateAsync({
        recipientId,
        body: { phoneInput: values.phoneInput, isPrimary: values.isPrimary },
      });
      toast.success('Phone number added');
      form.reset();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add phone';
      form.setError('phoneInput', { message: msg });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="add-phone-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add Phone Number</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => { void form.handleSubmit(onSubmit)(e); }}
          className="space-y-4"
        >
          <div className="space-y-1">
            <Label htmlFor="phoneInput">Phone Number</Label>
            <Input
              id="phoneInput"
              data-testid="field-phoneInput"
              placeholder="+91 98765-43210"
              {...form.register('phoneInput')}
            />
            {form.formState.errors.phoneInput !== undefined && (
              <p className="text-xs text-destructive" data-testid="error-phoneInput">
                {form.formState.errors.phoneInput.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Include country code (e.g. +91). Digits only after stripping +/- spaces.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="isPrimary"
              data-testid="field-isPrimary"
              checked={form.watch('isPrimary')}
              onCheckedChange={(v) => { form.setValue('isPrimary', v === true); }}
            />
            <Label htmlFor="isPrimary">Set as primary</Label>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={addMutation.isPending}
              data-testid="add-phone-submit-btn"
            >
              Add Phone
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
